import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

// Pages
import { LandingPage } from '@/pages/LandingPage'
import { AvailabilityLandingPage } from '@/pages/AvailabilityLandingPage'
import { VisibilityLandingPage } from '@/pages/VisibilityLandingPage'
import { CompetitorLandingPage } from '@/pages/CompetitorLandingPage'
import { PricingLandingPage } from '@/pages/PricingLandingPage'
import { PerformanceLandingPage } from '@/pages/PerformanceLandingPage'
import { CompetitorPage } from '@/pages/app/CompetitorPage'
import { ProductDetailPage } from '@/pages/app/ProductDetailPage'
import { PerformancePage } from '@/pages/app/PerformancePage'
import { StoreMapsPage } from '@/pages/app/StoreMapsPage'
import { AvailabilityPage } from '@/pages/app/AvailabilityPage'
import { VisibilityPage } from '@/pages/app/VisibilityPage'
import { SettingsPage } from '@/pages/app/SettingsPage'
import { Login } from '@/components/auth/Login'

// Onboarding Pages
import { OnboardingLayout, SetupPage, PersonalisationPage, WelcomePage } from '@/pages/app/onboarding'

// Workspace Pages
import { CreateWorkspacePage, AcceptInvitePage } from '@/pages/app/workspace'

// Settings Pages
import {
  WorkspaceGeneralPage,
  WorkspaceTeamPage,
  WorkspaceInvitesPage,
  WorkspaceBillingPage,
  AvailabilitySettingsPage,
  VisibilitySettingsPage,
  JobLogsPage,
  GeoMappingsSettingsPage,
} from '@/pages/app/settings'

// Layout
import { AppLayout } from '@/components/layout/AppLayout'
import { SettingsLayout } from '@/components/layout/SettingsLayout'

// Components
import { Skeleton } from '@/components/ui/skeleton'
import { PageTransition } from '@/components/PageTransition'

function AppRoutes() {
  const { loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Skeleton className="h-12 w-12 rounded-full" />
      </div>
    )
  }

  return (
    <Routes>
      {/* Landing Pages with Transitions */}
      <Route path="/" element={<PageTransition><LandingPage /></PageTransition>} />
      <Route path="/competitor" element={<PageTransition><CompetitorLandingPage /></PageTransition>} />
      <Route path="/availability" element={<PageTransition><AvailabilityLandingPage /></PageTransition>} />
      <Route path="/visibility" element={<PageTransition><VisibilityLandingPage /></PageTransition>} />
      <Route path="/pricing" element={<PageTransition><PricingLandingPage /></PageTransition>} />
      <Route path="/performance" element={<PageTransition><PerformanceLandingPage /></PageTransition>} />

      {/* Login Page */}
      <Route path="/login" element={<Login />} />

      {/* Onboarding Flow (outside AppLayout for full-screen experience) */}
      <Route path="/app/onboarding" element={<OnboardingLayout />}>
        <Route index element={<Navigate to="/app/onboarding/welcome" replace />} />
        <Route path="setup" element={<SetupPage />} />
        <Route path="personalisation" element={<PersonalisationPage />} />
        <Route path="welcome" element={<WelcomePage />} />
      </Route>

      {/* App Routes (Authenticated) */}
      <Route path="/app" element={<AppLayout />}>
        {/* Default redirect to competitor */}
        <Route index element={<Navigate to="/app/competitor" replace />} />

        {/* Competitor Module */}
        <Route path="competitor" element={<CompetitorPage />} />
        <Route path="competitor/product/:productId" element={<ProductDetailPage />} />

        {/* Availability Module */}
        <Route path="availability" element={<AvailabilityPage />} />

        {/* Visibility Module */}
        <Route path="visibility" element={<VisibilityPage />} />

        {/* Performance Module */}
        <Route path="performance" element={<PerformancePage />} />

        {/* Store Maps Module */}
        <Route path="store-maps" element={<StoreMapsPage />} />

        {/* Settings with nested routes */}
        <Route path="settings" element={<SettingsLayout />}>
          {/* App Settings */}
          <Route index element={<SettingsPage />} />
          <Route path="availability" element={<AvailabilitySettingsPage />} />
          <Route path="visibility" element={<VisibilitySettingsPage />} />
          <Route path="job-logs" element={<JobLogsPage />} />
          <Route path="geo-mappings" element={<GeoMappingsSettingsPage />} />

          {/* Workspace Settings */}
          <Route path="workspace" element={<WorkspaceGeneralPage />} />
          <Route path="workspace/team" element={<WorkspaceTeamPage />} />
          <Route path="workspace/invites" element={<WorkspaceInvitesPage />} />
          <Route path="workspace/billing" element={<WorkspaceBillingPage />} />
        </Route>

        {/* Workspace */}
        <Route path="workspace/new" element={<CreateWorkspacePage />} />
      </Route>

      {/* Accept Invite (outside AppLayout for full-screen experience) */}
      <Route path="/invites/:token/accept" element={<AcceptInvitePage />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  )
}

export default App
